var ref = require("./fragment");
var Fragment = ref.Fragment;
var ref$1 = require("./mark");
var Mark = ref$1.Mark;

// ParseRule:: interface
// A value that describes how to parse a given DOM node or inline
// style as a ProseMirror node or mark.
//
//   tag:: ?string
//   A CSS selector describing the kind of DOM elements to match. A
//   single rule should have _either_ a `tag` or a `style` property.
//
//   style:: ?string
//   A CSS property name to match. When given, this rule matches
//   inline styles that list that property.
//
//   node:: ?string
//   The name of the node type to create when this rule matches. Only
//   valid for rules with a `tag` property, not for style rules. Each
//   rule should have one of a `node`, `mark`, or `ignore` property
//   (except when it appears in a [node](#model.NodeSpec.parseDOM) or
//   [mark spec](#model.MarkSpec.parseDOM), in which case the `node`
//   or `mark` property will be derived from its position).
//
//   mark:: ?string
//   The name of the mark type to wrap the matched content in.
//
//   ignore:: ?bool
//   When true, ignore content that matches this rule.
//
//   attrs:: ?Object
//   Attributes for the node or mark created by this rule. When
//   `getAttrs` is provided, it takes precedence.
//
//   getAttrs:: ?(union<dom.Node, string>) → ?union<bool, Object>
//   A function used to compute the attributes for the node or mark
//   created by this rule. Can also be used to describe further
//   conditions the DOM element or style must match. When it returns
//   `false`, the rule won't match. When it returns null or undefined,
//   that is interpreted as an empty/default set of attributes.
//
//   Called with a DOM Element for `tag` rules, and with a string (the
//   style's value) for `style` rules.
//
//   contentElement:: ?string
//   For `tag` rules that produce non-leaf nodes or marks, by default
//   the content of the DOM element is parsed as content of the mark
//   or node. If the child nodes are in a descendent node, this may be
//   a CSS selector string that the parser must use to find the actual
//   content element.
//
//   preserveWhitespace:: ?bool
//   Controls whether whitespace should be preserved when parsing the
//   content inside the matched element.

// ::- A DOM parser represents a strategy for parsing DOM content into
// a ProseMirror document conforming to a given schema. Its behavior
// is defined by an array of [rules](#model.ParseRule).
var DOMParser = function DOMParser(schema, rules) {
  var this$1 = this;

  // :: Schema
  this.schema = schema
  // :: [ParseRule]
  this.rules = rules
  this.tags = []
  this.styles = []

  rules.forEach(function (rule) {
    if (rule.tag) { this$1.tags.push(rule) }
    else if (rule.style) { this$1.styles.push(rule) }
  })
};

// :: (dom.Node, ?Object) → Node
// Parse a document from the content of a DOM node. To provide an
// explicit parent document (for example, when not in a browser
// window environment, where we simply use the global document),
// pass it as the `document` property of `options`.
DOMParser.prototype.parse = function parse (dom, options) {
    if ( options === void 0 ) options = {};

  var topNode = options.topNode
  var top = new NodeBuilder(topNode ? topNode.type : this.schema.nodes.doc,
                            topNode ? topNode.attrs : null, true, null, null, options.preserveWhitespace)
  var state = new DOMParseState(this, options, top)
  state.addAll(dom, null, options.from, options.to)
  return top.finish()
};

// : (ResolvedPos, dom.Node, ?Object) → Slice
// Parse a DOM fragment into a `Slice`, starting with the context at
// `$context`. If the DOM nodes are known to be 'open' (as in
// `Slice`), pass their left open depth as the `openLeft` option.
DOMParser.prototype.parseInContext = function parseInContext ($context, dom, options) {
    if ( options === void 0 ) options = {};

  var ref = builderFromContext($context, options.preserveWhitespace);
    var builder = ref.builder;
    var top = ref.top;
  var openLeft = options.openLeft, startPos = $context.depth

  new (function (DOMParseState) {
      function anonymous () {
        DOMParseState.apply(this, arguments);
      }

      if ( DOMParseState ) anonymous.__proto__ = DOMParseState;
      anonymous.prototype = Object.create( DOMParseState && DOMParseState.prototype );
      anonymous.prototype.constructor = anonymous;

      anonymous.prototype.enter = function enter (type, attrs, preserveWhitespace) {
      if (openLeft == null) { openLeft = type.isTextblock ? 1 : 0 }
      if (openLeft > 0 && this.top.match.matchType(type, attrs)) { openLeft = 0 }
      if (openLeft == 0) { return DOMParseState.prototype.enter.call(this, type, attrs, preserveWhitespace) }

      openLeft--
      return null
    };

      return anonymous;
    }(DOMParseState))(this, options, builder).addAll(dom)

  var openTo = top.openDepth, doc = top.finish(openTo), $startPos = doc.resolve(startPos)
  for (var d = $startPos.depth; d >= 0 && startPos == $startPos.end(d); d--) { ++startPos }
  return doc.slice(startPos, doc.content.size - openTo)
};

DOMParser.prototype.matchTag = function matchTag (dom) {
    var this$1 = this;

  for (var i = 0; i < this.tags.length; i++) {
    var rule = this$1.tags[i]
    if (matches(dom, rule.tag)) {
      if (rule.getAttrs) {
        var result = rule.getAttrs(dom)
        if (result === false) { continue }
        rule.attrs = result
      }
      return rule
    }
  }
};

DOMParser.prototype.matchStyle = function matchStyle (prop, value) {
    var this$1 = this;

  for (var i = 0; i < this.styles.length; i++) {
    var rule = this$1.styles[i]
    if (rule.style == prop) {
      if (rule.getAttrs) {
        var result = rule.getAttrs(value)
        if (result === false) { continue }
        rule.attrs = result
      }
      return rule
    }
  }
};

// :: (Schema) → [ParseRule]
// Extract the parse rules listed in a schema's [node
// specs](#model.NodeSpec.parseDOM).
DOMParser.schemaRules = function schemaRules (schema) {
  var result = []
  var loop = function ( name ) {
    var rules = schema.marks[name].spec.parseDOM
    if (rules) { rules.forEach(function (rule) {
      result.push(rule = copy(rule))
      rule.mark = name
    }) }
  };

    for (var name in schema.marks) loop( name );
  var loop$1 = function ( name ) {
    var rules$1 = schema.nodes[name$1].spec.parseDOM
    if (rules$1) { rules$1.forEach(function (rule) {
      result.push(rule = copy(rule))
      rule.node = name$1
    }) }
  };

    for (var name$1 in schema.nodes) loop$1( name );
  return result
};

// :: (Schema) → DOMParser
// Construct a DOM parser using the parsing rules listed in a
// schema's [node specs](#model.NodeSpec.parseDOM).
DOMParser.fromSchema = function fromSchema (schema) {
  return schema.cached.domParser ||
    (schema.cached.domParser = new DOMParser(schema, DOMParser.schemaRules(schema)))
};
exports.DOMParser = DOMParser

function builderFromContext($context, preserveWhitespace) {
  var top, builder
  for (var i = 0; i <= $context.depth; i++) {
    var node = $context.node(i), match = node.contentMatchAt($context.index(i))
    if (i == 0)
      { builder = top = new NodeBuilder(node.type, node.attrs, true, null, match, preserveWhitespace) }
    else
      { builder = builder.start(node.type, node.attrs, false, match, preserveWhitespace) }
  }
  return {builder: builder, top: top}
}

var NodeBuilder = function NodeBuilder(type, attrs, solid, prev, match, preserveWhitespace) {
  // : NodeType
  // The type of the node being built
  this.type = type
  // : ContentMatch
  // The content match at this point, used to determine whether
  // other nodes may be added here.
  this.match = match || type.contentExpr.start(attrs)
  // : bool
  // True when the node is found in the source, and thus should be
  // preserved until its end. False when it was made up to provide a
  // wrapper for another node.
  this.solid = solid
  // : [Node]
  // The nodes that have been added so far.
  this.content = []
  // : ?NodeBuilder
  // The builder for the parent node, if any.
  this.prev = prev
  // : ?NodeBuilder
  // The builder for the last child, if that is still open (see
  // `NodeBuilder.start`)
  this.openChild = null
  // : bool
  this.preserveWhitespace = preserveWhitespace
};

var prototypeAccessors = { depth: {},openDepth: {},posBeforeLastChild: {},currentPos: {} };

// : (Node) → ?Node
// Try to add a node. Strip it of marks if necessary. Returns null
// when the node doesn't fit here.
NodeBuilder.prototype.add = function add (node) {
    var this$1 = this;

  var matched = this.match.matchNode(node)
  if (!matched && node.marks.length) {
    node = node.mark(node.marks.filter(function (mark) { return this$1.match.allowsMark(mark.type); }))
    matched = this.match.matchNode(node)
  }
  if (!matched) { return null }
  this.closeChild()
  this.content.push(node)
  this.match = matched
  return node
};

// : (NodeType, ?Object, bool, ?ContentMatch) → ?NodeBuilder
// Try to start a new node at this point.
NodeBuilder.prototype.start = function start (type, attrs, solid, match, preserveWhitespace) {
  var matched = this.match.matchType(type, attrs)
  if (!matched) { return null }
  this.closeChild()
  this.match = matched
  return this.openChild = new NodeBuilder(type, attrs, solid, this, match, preserveWhitespace)
};

NodeBuilder.prototype.closeChild = function closeChild (openRight) {
  if (this.openChild) {
    this.content.push(this.openChild.finish(openRight && openRight - 1))
    this.openChild = null
  }
};

// : ()
// Strip any trailing space text from the builder's content.
NodeBuilder.prototype.stripTrailingSpace = function stripTrailingSpace () {
  if (this.openChild) { return }
  var last = this.content[this.content.length - 1], m
  if (last && last.isText && (m = /\s+$/.exec(last.text))) {
    if (last.text.length == m[0].length) { this.content.pop() }
    else { this.content[this.content.length - 1] = last.withText(last.text.slice(0, last.text.length - m[0].length)) }
  }
};

// : (?number) → Node
// Finish this node. If `openRight` is > 0, the node (and `openRight
// - 1` last children) is partial, and we don't need to 'close' it
// by filling in required content.
NodeBuilder.prototype.finish = function finish (openRight) {
  if (!openRight && !this.preserveWhitespace) { this.stripTrailingSpace() }
  this.closeChild(openRight)
  var content = Fragment.from(this.content)
  if (!openRight) { content = content.append(this.match.fillBefore(Fragment.empty, true)) }
  return this.type.create(this.match.attrs, content)
};

// : (NodeType, ?Object, ?Node) → ?NodeBuilder
// Try to find a valid place to add a node with the given type and
// attributes. When successful, if `node` was given, add it in its
// entirety and return the builder to which it was added. If not,
// start a node of the given type and return the builder for it.
NodeBuilder.prototype.findPlace = function findPlace (type, attrs, node, preserveWhitespace) {
  var route, builder
  for (var top = this;; top = top.prev) {
    var found = top.match.findWrapping(type, attrs)
    if (found && (!route || route.length > found.length)) {
      route = found
      builder = top
      if (!found.length) { break }
    }
    if (top.solid) { break }
  }

  if (!route) { return null }
  for (var i = 0; i < route.length; i++)
    { builder = builder.start(route[i].type, route[i].attrs, false) }
  return node ? builder.add(node) && builder : builder.start(type, attrs, true, null, preserveWhitespace)
};

prototypeAccessors.depth.get = function () {
  var d = 0
  for (var b = this.prev; b; b = b.prev) { d++ }
  return d
};

prototypeAccessors.openDepth.get = function () {
  var d = 0
  for (var c = this.openChild; c; c = c.openChild) { d++ }
  return d
};

prototypeAccessors.posBeforeLastChild.get = function () {
    var this$1 = this;

  var pos = this.prev ? this.prev.posBeforeLastChild + 1 : 0
  for (var i = 0; i < this.content.length; i++)
    { pos += this$1.content[i].nodeSize }
  return pos
};

prototypeAccessors.currentPos.get = function () {
  this.closeChild()
  return this.posBeforeLastChild
};

Object.defineProperties( NodeBuilder.prototype, prototypeAccessors );

// : Object<bool> The block-level tags in HTML5
var blockTags = {
  address: true, article: true, aside: true, blockquote: true, canvas: true,
  dd: true, div: true, dl: true, fieldset: true, figcaption: true, figure: true,
  footer: true, form: true, h1: true, h2: true, h3: true, h4: true, h5: true,
  h6: true, header: true, hgroup: true, hr: true, li: true, noscript: true, ol: true,
  output: true, p: true, pre: true, section: true, table: true, tfoot: true, ul: true
}

// : Object<bool> The tags that we normally ignore.
var ignoreTags = {
  head: true, noscript: true, object: true, script: true, style: true, title: true
}

// : Object<bool> List tags.
var listTags = {ol: true, ul: true}

// A state object used to track context during a parse.
var DOMParseState = function DOMParseState(parser, options, top) {
  // : Object The options passed to this parse.
  this.options = options || {}
  // : DOMParser The parser we are using.
  this.parser = parser
  this.top = top
  // : [Mark] The current set of marks
  this.marks = Mark.none
  this.find = options.findPositions
};

// : (Mark) → [Mark]
// Add a mark to the current set of marks, return the old set.
DOMParseState.prototype.addMark = function addMark (mark) {
  var old = this.marks
  this.marks = mark.addToSet(this.marks)
  return old
};

// : (dom.Node)
// Add a DOM node to the content. Text is inserted as text node,
// otherwise, the node is passed to `addElement` or, if it has a
// `style` attribute, `addElementWithStyles`.
DOMParseState.prototype.addDOM = function addDOM (dom) {
    var this$1 = this;

  if (dom.nodeType == 3) {
    var value = dom.nodeValue
    var top = this.top
    if (/\S/.test(value) || top.type.isTextblock) {
      if (!this.top.preserveWhitespace) {
        value = value.replace(/\s+/g, " ")
        // If this starts with whitespace, and there is either no node
        // before it or a node that ends with whitespace, strip the
        // leading space.
        if (/^\s/.test(value)) { top.stripTrailingSpace() }
      }
      if (value)
        { this.insertNode(this.parser.schema.text(value, this.marks)) }
      this.findInText(dom)
    } else {
      this.findInside(dom)
    }
  } else if (dom.nodeType == 1 && !dom.hasAttribute("pm-ignore")) {
    if (dom.hasAttribute("pm-decoration")) {
      for (var child = dom.firstChild; child; child = child.nextSibling)
        { this$1.addDOM(child) }
    } else {
      var style = dom.getAttribute("style")
      if (style) { this.addElementWithStyles(parseStyles(style), dom) }
      else { this.addElement(dom) }
    }
  }
};

// : (dom.Node)
// Try to find a handler for the given tag and use that to parse. If
// none is found, the element's content nodes are added directly.
DOMParseState.prototype.addElement = function addElement (dom) {
  var name = dom.nodeName.toLowerCase()
  if (listTags.hasOwnProperty(name)) { this.normalizeList(dom) }
  // Ignore trailing BR nodes, which browsers create during editing
  if (this.options.editableContent && name == "br" && !dom.nextSibling) { return }
  if (!this.parseNodeType(dom, name)) {
    if (ignoreTags.hasOwnProperty(name)) {
      this.findInside(dom)
    } else {
      var sync = blockTags.hasOwnProperty(name) && this.top
      this.addAll(dom)
      if (sync) { this.sync(sync) }
    }
  }
};

// Run any style parser associated with the node's styles. After
// that, if no style parser suppressed the node's content, pass it
// through to `addElement`.
DOMParseState.prototype.addElementWithStyles = function addElementWithStyles (styles, dom) {
    var this$1 = this;

  var oldMarks = this.marks, marks = this.marks
  for (var i = 0; i < styles.length; i += 2) {
    var rule = this$1.parser.matchStyle(styles[i], styles[i + 1])
    if (!rule) { continue }
    if (rule.ignore) { return }
    marks = this$1.parser.schema.marks[rule.mark].create(rule.attrs).addToSet(marks)
  }
  this.marks = marks
  this.addElement(dom)
  this.marks = oldMarks
};

// (dom.Node, string) → bool
// Look up a handler for the given node. If none are found, return
// false. Otherwise, apply it, use its return value to drive the way
// the node's content is wrapped, and return true.
DOMParseState.prototype.parseNodeType = function parseNodeType (dom) {
  var rule = this.parser.matchTag(dom)
  if (!rule) { return false }
  if (rule.ignore) { return true }

  var sync, before, nodeType, markType
  var contentDOM = (rule.contentElement && dom.querySelector(rule.contentElement)) || dom

  if (rule.node) {
    nodeType = this.parser.schema.nodes[rule.node]
    if (nodeType.isLeaf) { this.insertNode(nodeType.create(rule.attrs)) }
    else { sync = this.enter(nodeType, rule.attrs, rule.preserveWhitespace) }
  } else {
    markType = this.parser.schema.marks[rule.mark]
    before = this.addMark(markType.create(rule.attrs))
  }

  if (markType || !nodeType.isLeaf) {
    this.findAround(dom, contentDOM, true)
    this.addAll(contentDOM, sync)
    if (sync) { this.sync(sync.prev) }
    else if (before) { this.marks = before }
    this.findAround(dom, contentDOM, true)
  } else {
    this.findInside(dom)
  }
  return true
};

// : (dom.Node, ?NodeBuilder, ?number, ?number)
// Add all child nodes between `startIndex` and `endIndex` (or the
// whole node, if not given). If `sync` is passed, use it to
// synchronize after every block element.
DOMParseState.prototype.addAll = function addAll (parent, sync, startIndex, endIndex) {
    var this$1 = this;

  var index = startIndex || 0
  for (var dom = startIndex ? parent.childNodes[startIndex] : parent.firstChild,
           end = endIndex == null ? null : parent.childNodes[endIndex];
       dom != end; dom = dom.nextSibling, ++index) {
    this$1.findAtPoint(parent, index)
    this$1.addDOM(dom)
    if (sync && blockTags.hasOwnProperty(dom.nodeName.toLowerCase()))
      { this$1.sync(sync) }
  }
  this.findAtPoint(parent, index)
};

// : (Node) → ?Node
// Try to insert the given node, adjusting the context when needed.
DOMParseState.prototype.insertNode = function insertNode (node) {
  var ok = this.top.findPlace(node.type, node.attrs, node)
  if (ok) {
    this.sync(ok)
    return true
  }
};

// : (NodeType, ?Object) → ?NodeBuilder
// Try to start a node of the given type, adjusting the context when
// necessary.
DOMParseState.prototype.enter = function enter (type, attrs, preserveWhitespace) {
  var ok = this.top.findPlace(type, attrs, null, preserveWhitespace)
  if (ok) {
    this.sync(ok)
    return ok
  }
};

// : ()
// Leave the node currently at the top.
DOMParseState.prototype.leave = function leave () {
  this.top = this.top.prev
};

DOMParseState.prototype.sync = function sync (to) {
    var this$1 = this;

  for (;;) {
    for (var cur = to; cur; cur = cur.prev) { if (cur == this$1.top) {
      this$1.top = to
      return
    } }
    this$1.leave()
  }
};

// Kludge to work around directly nested list nodes produced by some
// tools and allowed by browsers to mean that the nested list is
// actually part of the list item above it.
DOMParseState.prototype.normalizeList = function normalizeList (dom) {
  for (var child = dom.firstChild, prevItem = null; child; child = child.nextSibling) {
    var name = child.nodeType == 1 ? child.nodeName.toLowerCase() : null
    if (name && listTags.hasOwnProperty(name) && prevItem) {
      prevItem.appendChild(child)
      child = prevItem
    } else if (name == "li") {
      prevItem = child
    } else if (name) {
      prevItem = null
    }
  }
};

DOMParseState.prototype.findAtPoint = function findAtPoint (parent, offset) {
    var this$1 = this;

  if (this.find) { for (var i = 0; i < this.find.length; i++) {
    if (this$1.find[i].node == parent && this$1.find[i].offset == offset)
      { this$1.find[i].pos = this$1.top.currentPos }
  } }
};

DOMParseState.prototype.findInside = function findInside (parent) {
    var this$1 = this;

  if (this.find) { for (var i = 0; i < this.find.length; i++) {
    if (this$1.find[i].pos == null && parent.contains(this$1.find[i].node))
      { this$1.find[i].pos = this$1.top.currentPos }
  } }
};

DOMParseState.prototype.findAround = function findAround (parent, content, before) {
    var this$1 = this;

  if (parent != content && this.find) { for (var i = 0; i < this.find.length; i++) {
    if (this$1.find[i].pos == null && parent.contains(this$1.find[i].node)) {
      var pos = content.compareDocumentPosition(this$1.find[i].node)
      if (pos & (before ? 2 : 4))
        { this$1.find[i].pos = this$1.top.currentPos }
    }
  } }
};

DOMParseState.prototype.findInText = function findInText (textNode) {
    var this$1 = this;

  if (this.find) { for (var i = 0; i < this.find.length; i++) {
    if (this$1.find[i].node == textNode)
      { this$1.find[i].pos = this$1.top.currentPos - (textNode.nodeValue.length - this$1.find[i].offset) }
  } }
};

// Apply a CSS selector.
function matches(dom, selector) {
  return (dom.matches || dom.msMatchesSelector || dom.webkitMatchesSelector || dom.mozMatchesSelector).call(dom, selector)
}

// : (string) → [string]
// Tokenize a style attribute into property/value pairs.
function parseStyles(style) {
  var re = /\s*([\w-]+)\s*:\s*([^;]+)/g, m, result = []
  while (m = re.exec(style)) { result.push(m[1], m[2].trim()) }
  return result
}

function copy(obj) {
  var copy = {}
  for (var prop in obj) { copy[prop] = obj[prop] }
  return copy
}



//////////////////
// WEBPACK FOOTER
// ../~/prosemirror-model/dist/from_dom.js
// module id = 838
// module chunks = 0